import { WallDashPattern, WallDoor } from '../types';
import {
  bezierArcLength,
  bezierParameterAtDistance
} from './bezier';

/** Returns a Konva/SVG-compatible dash array for the given pattern and wall thickness. */
export const getWallDash = (
  pattern: WallDashPattern | undefined,
  thickness: number
): number[] | undefined => {
  if (!pattern || pattern === 'solid') return undefined;
  if (pattern === 'dashed') return [thickness * 2, thickness];
  // dotted
  return [2, thickness * 1.2];
};

/** Compute solid wall segments with gaps cut out for doors. Returns [startDist, endDist] pairs along the wall. */
export const getWallSegments = (
  doors: WallDoor[] | undefined,
  wallLength: number
): [number, number][] => {
  if (!doors || doors.length === 0) {
    return [[0, wallLength]];
  }

  // Create sorted door gaps
  const gaps = doors
    .map((door) => {
      const center = door.position * wallLength;
      const half = door.width / 2;
      return [
        Math.max(0, center - half),
        Math.min(wallLength, center + half)
      ] as [number, number];
    })
    .sort((a, b) => a[0] - b[0]);

  // Merge overlapping gaps
  const merged: [number, number][] = [];
  for (const gap of gaps) {
    if (merged.length > 0 && gap[0] <= merged[merged.length - 1][1]) {
      merged[merged.length - 1][1] = Math.max(
        merged[merged.length - 1][1],
        gap[1]
      );
    } else {
      merged.push([...gap]);
    }
  }

  // Build solid segments between gaps
  const segments: [number, number][] = [];
  let cursor = 0;
  for (const [gapStart, gapEnd] of merged) {
    if (cursor < gapStart) {
      segments.push([cursor, gapStart]);
    }
    cursor = gapEnd;
  }
  if (cursor < wallLength) {
    segments.push([cursor, wallLength]);
  }

  return segments;
};

/** For curved walls: get bezier parameter ranges [tStart, tEnd] for each solid segment */
export const getCurvedWallSegmentParams = (
  doors: WallDoor[] | undefined,
  x1: number,
  y1: number,
  cx: number,
  cy: number,
  x2: number,
  y2: number
): [number, number][] => {
  const arcLen = bezierArcLength(x1, y1, cx, cy, x2, y2);
  const distSegments = getWallSegments(doors, arcLen);
  return distSegments.map(([startDist, endDist]) => [
    bezierParameterAtDistance(x1, y1, cx, cy, x2, y2, startDist),
    bezierParameterAtDistance(x1, y1, cx, cy, x2, y2, endDist)
  ]);
};
